import {dropbox_os} from '@dropbox/api-v2-client';
import {Button} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split, Stack} from '@dropbox/dig-foundations';
import {useInfiniteQuery} from '@tanstack/react-query';
import {DropboxClientContext} from 'App';
import {snackbarAtom} from 'atoms/snackbar';
import {Layout} from 'components/DSYS/Layout';
import {useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {ReactNode, useContext, useEffect, useMemo, useState} from 'react';

import {
  allAppsQueryKey,
  allReplitAppsQueryKey,
  listApps,
  listMyApps,
  listMyReplitApps,
  listReplitApps,
  myAppsQueryKey,
  myReplitAppsQueryKey,
} from './api';
import {AppCatalogSkeleton} from './AppCatalogSkeleton';
import {AppOrgShelves} from './AppOrgShelves';
import styles from './Apps.module.css';
import {AppsCatalogHeader} from './AppsCatalogHeader';
import {AppTileGrid} from './AppTileGrid';
import {CatalogApp, fromReplitCatalogApp} from './catalogApp';
import {searchCatalogApps} from './catalogFilters';
import {buildCatalogOrgSections, CatalogOrgSection, OTHER_ORG_ID} from './catalogOrgs';
import {useCatalogAuthorDirectory} from './useCatalogAuthorDirectory';

const MY_APPS_SECTION_ID = 'mine';

const CatalogError = ({message, retry}: {message: string; retry: () => void}) => (
  <Box paddingTop="12">
    <Split alignY="center" gap="16" role="alert">
      <Split.Item width="fill">
        <Text color="subtle">{message}</Text>
      </Split.Item>
      <Split.Item>
        <Button size="small" variant="outline" onClick={retry}>
          {t('retry')}
        </Button>
      </Split.Item>
    </Split>
  </Box>
);

const AppsSection = ({
  apps,
  content,
  emptyAction,
  emptyDescription,
  error,
  partialError = false,
  fetchNextPage,
  hasNextPage,
  heading,
  isFetchingNextPage,
  isLoading,
  retry,
}: {
  apps: CatalogApp[];
  /** Replaces the default tile grid once apps have loaded. */
  content?: ReactNode;
  emptyAction?: ReactNode;
  emptyDescription: string;
  error: boolean;
  partialError?: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  heading: string;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  retry: () => void;
}) => {
  const fadeInRef = useSkeletonFadeIn<HTMLElement>(isLoading);
  return (
    <section ref={fadeInRef} aria-label={heading}>
      {!isLoading && !error && partialError ? (
        <CatalogError message={t('apps_partial_error_description')} retry={retry} />
      ) : null}
      {isLoading ? (
        <AppCatalogSkeleton />
      ) : error ? (
        <CatalogError message={t('apps_error_description')} retry={retry} />
      ) : apps.length === 0 && !hasNextPage ? (
        <Box paddingY="12">
          <Stack gap="8">
            <Text color="subtle">{emptyDescription}</Text>
            {emptyAction}
          </Stack>
        </Box>
      ) : (
        <Stack gap="24">
          {content ?? <AppTileGrid apps={apps} />}
          {hasNextPage ? (
            <Box paddingTop="12">
              <Button
                variant="transparent"
                size="small"
                aria-busy={isFetchingNextPage}
                onClick={() => {
                  if (isFetchingNextPage) {
                    return;
                  }
                  fetchNextPage();
                }}
              >
                {isFetchingNextPage ? t('loading_more') : t('load_more')}
              </Button>
            </Box>
          ) : null}
        </Stack>
      )}
    </section>
  );
};

export const AppsCatalog: React.FC = () => {
  const client = useContext(DropboxClientContext);
  const setSnackbar = useSetAtom(snackbarAtom);
  const [queryText, setQueryText] = useState('');
  useDocumentTitle(t('apps'));

  const myAppsQuery = useInfiniteQuery({
    // The authenticated client is stable application context, not catalog identity.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: myAppsQueryKey,
    initialPageParam: undefined as string | undefined,
    enabled: client !== null,
    queryFn: ({pageParam}) => {
      if (!client) {
        throw new Error('Dropbox client is not ready');
      }
      return listMyApps(client, pageParam);
    },
    getNextPageParam: (lastPage: dropbox_os.ListAppsResponse) =>
      lastPage.next_page_token || undefined,
  });

  const allAppsQuery = useInfiniteQuery({
    // The authenticated client is stable application context, not catalog identity.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: allAppsQueryKey,
    initialPageParam: undefined as string | undefined,
    enabled: client !== null,
    queryFn: ({pageParam}) => {
      if (!client) {
        throw new Error('Dropbox client is not ready');
      }
      return listApps(client, pageParam);
    },
    getNextPageParam: (lastPage: dropbox_os.ListAppsResponse) =>
      lastPage.next_page_token || undefined,
  });

  const myReplitAppsQuery = useInfiniteQuery({
    queryKey: myReplitAppsQueryKey,
    initialPageParam: undefined as string | undefined,
    enabled: client !== null,
    queryFn: ({pageParam}) => listMyReplitApps(pageParam),
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
  });

  const allReplitAppsQuery = useInfiniteQuery({
    queryKey: allReplitAppsQueryKey,
    initialPageParam: undefined as string | undefined,
    enabled: client !== null,
    queryFn: ({pageParam}) => listReplitApps(pageParam),
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
  });
  const catalogQueries = [allAppsQuery, allReplitAppsQuery, myAppsQuery, myReplitAppsQuery];

  const myApps: CatalogApp[] = useMemo(
    () => [
      ...(myAppsQuery.data?.pages.flatMap((page) => page.apps ?? []) ?? []),
      ...(myReplitAppsQuery.data?.pages.flatMap((page) => page.apps) ?? []).map(
        fromReplitCatalogApp
      ),
    ],
    [myAppsQuery.data, myReplitAppsQuery.data]
  );
  const allApps: CatalogApp[] = useMemo(
    () => [
      ...(allAppsQuery.data?.pages.flatMap((page) => page.apps ?? []) ?? []),
      ...(allReplitAppsQuery.data?.pages.flatMap((page) => page.apps) ?? []).map(
        fromReplitCatalogApp
      ),
    ],
    [allAppsQuery.data, allReplitAppsQuery.data]
  );

  // Shelves need the whole catalog, so every page of each list loads up front.
  const isListLoading = (query: (typeof catalogQueries)[number]) =>
    query.isPending || (query.hasNextPage && !query.isFetchNextPageError);
  const nextPageFailed = catalogQueries.some((query) => query.isFetchNextPageError);
  const allAppsLoading = isListLoading(allAppsQuery) || isListLoading(allReplitAppsQuery);
  const myAppsLoading = isListLoading(myAppsQuery) || isListLoading(myReplitAppsQuery);

  useEffect(() => {
    for (const query of catalogQueries) {
      if (query.hasNextPage && !query.isFetchingNextPage && !query.isFetchNextPageError) {
        void query.fetchNextPage();
      }
    }
  });

  useEffect(() => {
    if (nextPageFailed) {
      setSnackbar({text: t('apps_pagination_error')});
    }
  }, [nextPageFailed, setSnackbar]);

  const authorDirectory = useCatalogAuthorDirectory({
    apps: allApps,
    enabled: !allAppsLoading,
  });

  const sections: CatalogOrgSection[] = useMemo(() => {
    const orgSections = buildCatalogOrgSections(
      allApps,
      authorDirectory.orgByLdap,
      authorDirectory.viewerOrg
    );
    // Without any known org, the lone catch-all shelf is simply everything.
    const browseSections =
      orgSections.length === 1 && orgSections[0].id === OTHER_ORG_ID
        ? [{...orgSections[0], label: t('apps_all_apps')}]
        : orgSections;
    if (!myApps.length) return browseSections;
    return [
      {
        apps: myApps,
        id: MY_APPS_SECTION_ID,
        isViewerOrg: false,
        label: t('apps_my_apps'),
        withActions: true,
      },
      ...browseSections,
    ];
  }, [allApps, authorDirectory.orgByLdap, authorDirectory.viewerOrg, myApps]);

  const isSearching = Boolean(queryText.trim());
  // Search narrows each shelf in place, so matches stay grouped by org.
  const visibleSections = useMemo(
    () =>
      isSearching
        ? sections.map((section) => ({
            ...section,
            apps: searchCatalogApps(section.apps, queryText, authorDirectory.nameByLdap),
          }))
        : sections,
    [authorDirectory.nameByLdap, isSearching, queryText, sections]
  );
  const visibleApps = visibleSections.flatMap((section) => section.apps);
  const allAppsFailed = allAppsQuery.isError && allReplitAppsQuery.isError;
  const partialError = !allAppsFailed && catalogQueries.some((query) => query.isError);

  const content: ReactNode =
    visibleSections.length > 1 ? (
      <AppOrgShelves sections={visibleSections} />
    ) : visibleSections[0] ? (
      // A single shelf is just a worse grid.
      <AppTileGrid apps={visibleSections[0].apps} withActions={visibleSections[0].withActions} />
    ) : undefined;

  return (
    <Layout.Container>
      <div className={styles.appCatalog}>
        <AppsCatalogHeader query={queryText} onQueryChange={setQueryText} />
        <AppsSection
          apps={visibleApps}
          content={content}
          emptyAction={
            isSearching ? (
              <div>
                <Button size="small" variant="outline" onClick={() => setQueryText('')}>
                  {t('apps_clear_search')}
                </Button>
              </div>
            ) : undefined
          }
          emptyDescription={
            isSearching ? t('apps_search_empty_description') : t('apps_all_empty_description')
          }
          error={allAppsFailed && !myApps.length}
          partialError={partialError}
          fetchNextPage={() => {
            for (const query of catalogQueries) {
              if (query.hasNextPage) void query.fetchNextPage();
            }
          }}
          hasNextPage={nextPageFailed && catalogQueries.some((query) => query.hasNextPage)}
          heading={t('apps_all_apps')}
          isFetchingNextPage={catalogQueries.some((query) => query.isFetchingNextPage)}
          isLoading={!client || allAppsLoading || myAppsLoading || authorDirectory.isLoading}
          retry={() => {
            for (const query of catalogQueries) void query.refetch();
          }}
        />
      </div>
    </Layout.Container>
  );
};
